import {
  QUESTION_CODE_RE,
  type RuleRequestView,
  type SetRuleRequestStatus,
} from '@tabel/shared/schemas/rules';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Inbox, ListChecks } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { useToast } from '../../components/ErrorToast';
import { Button, Card, EmptyState, Skeleton, inputCls, textareaCls } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { isTransient, reportClientError } from '../../lib/errors';
import { useRole } from '../../lib/role';
import { DescribeRule } from './DescribeRule';
import { DialogFooter, GuardedModal, type ModalControl, useDirty } from './GuardedModal';
import { LoadError, shouldReport } from './LoadError';
import { localDate } from './dates';
import { LABEL, T, say } from './texts';

/*
 * Вид «Просьбы о правилах» (004, US6, T100; contracts/ui.md, «Вид «Просьбы о правилах»»; FR-057; сценарии US6 5, 6):
 * текст, автор, дата, статус — «Ждёт разбора», «В реестре: Q-NN», «Отклонена: причина». Видят администратор правил,
 * утверждающий и администратор системы (сервер отдаёт просьбы в пределах прав). Администратор правил и утверждающий
 * описывают новое правило здесь же. Администратор системы (команда) ставит статус: «В реестре…» — номер вопроса,
 * который команда завела в файле реестра (он сразу появляется в «Связанном вопросе» публикации), «Отклонить…» —
 * причина.
 */

type Mode = SetRuleRequestStatus['status'];

const TONE: Readonly<Record<RuleRequestView['status'], string>> = {
  waiting: 'bg-warn-soft text-warn ring-warn/20',
  in_registry: 'bg-ok-soft text-ok ring-ok/20',
  rejected: 'bg-subtle text-ink-2 ring-line',
};

function statusText(r: RuleRequestView): string {
  if (r.status === 'in_registry') return say.inRegistry(r.questionCode ?? '');
  if (r.status === 'rejected') return say.rejected(r.rejectReason ?? '');
  return LABEL.waiting;
}

/** «30», «q30», «Q-30» → «Q-30»; другое — как введено. */
export function normalizeCode(raw: string): string {
  const s = raw.trim();
  const m = /^(?:[qQ]-?)?(\d{2,3})$/u.exec(s);
  return m ? `Q-${m[1]}` : s;
}

function StatusBody({
  request,
  mode,
  ctl,
  onDone,
}: {
  request: RuleRequestView;
  mode: Mode;
  ctl: ModalControl;
  onDone: () => void;
}) {
  const ids = useId();
  const { role } = useRole();
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const [value, setValue] = useState(mode === 'in_registry' ? (request.questionCode ?? '') : '');
  useDirty(ctl, value.trim() !== '' && value !== (request.questionCode ?? ''));
  const code = normalizeCode(value);
  const body: SetRuleRequestStatus | null =
    mode === 'in_registry'
      ? QUESTION_CODE_RE.test(code)
        ? { status: 'in_registry', questionCode: code }
        : null
      : value.trim()
        ? { status: 'rejected', reason: value.trim() }
        : null;

  const save = useMutation({
    mutationFn: (b: SetRuleRequestStatus) => api.setRuleRequestStatus(request.id, b),
    onSuccess: () => {
      showInfo(T.statusSaved);
      void qc.invalidateQueries({ queryKey: rulesKeys.requests(role) });
      // Номер из просьбы — сразу в «Связанном вопросе» публикации (`GET /api/rules/registry`).
      void qc.invalidateQueries({ queryKey: rulesKeys.registry(role) });
      onDone();
    },
    onError: (e) =>
      showError(e, {
        screen: 'Rules',
        action: 'rule request status',
        retry: isTransient(e) && body ? () => save.mutate(body) : undefined,
      }),
  });
  const reason = body ? null : mode === 'in_registry' ? T.whyCode : T.whyRejectReason;

  return (
    <div className="space-y-1.5 text-[13.5px]">
      <p className="mb-3 rounded-[8px] bg-subtle px-3 py-2 break-words whitespace-pre-wrap text-ink-2">
        {request.text}
      </p>
      <label htmlFor={`${ids}-v`} className="block font-medium text-ink">
        {mode === 'in_registry' ? LABEL.questionCode : LABEL.reason}
      </label>
      {mode === 'in_registry' ? (
        <>
          <input
            id={`${ids}-v`}
            type="text"
            data-autofocus
            autoComplete="off"
            maxLength={8}
            placeholder="Q-30"
            value={value}
            aria-describedby={`${ids}-hint`}
            onChange={(e) => setValue(e.target.value)}
            className={`${inputCls} max-w-[160px] tabular-nums`}
          />
          <p id={`${ids}-hint`} className="text-[12px] text-ink-3">
            {T.codeHint}
          </p>
        </>
      ) : (
        <textarea
          id={`${ids}-v`}
          data-autofocus
          rows={3}
          maxLength={500}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={textareaCls}
        />
      )}
      <DialogFooter reasonId={`${ids}-why`} reason={reason}>
        <Button onClick={ctl.requestClose}>{LABEL.cancel}</Button>
        <Button
          variant={mode === 'in_registry' ? 'primary' : 'danger'}
          icon={mode === 'in_registry' ? ListChecks : Ban}
          disabled={body === null || save.isPending}
          aria-describedby={reason ? `${ids}-why` : undefined}
          onClick={() => body && save.mutate(body)}
        >
          {mode === 'in_registry' ? LABEL.save : LABEL.rejectDo}
        </Button>
      </DialogFooter>
    </div>
  );
}

function Request({
  r,
  tzOffset,
  onStatus,
}: {
  r: RuleRequestView;
  tzOffset: string;
  onStatus: ((r: RuleRequestView, mode: Mode) => void) | null;
}) {
  return (
    <li className="space-y-2 px-5 py-4 max-sm:px-4">
      <p className="text-[14px] break-words whitespace-pre-wrap text-ink">{r.text}</p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12.5px] text-ink-2">
        <span>{say.requestBy(r.author.name, localDate(r.createdAt, tzOffset))}</span>
        <span
          className={`inline-flex max-w-full items-center rounded-full px-2 py-0.5 text-[12px] font-medium break-words ring-1 ring-inset ${TONE[r.status]}`}
        >
          {statusText(r)}
        </span>
      </div>
      {onStatus && (
        <div className="flex flex-wrap gap-2 pt-1 max-sm:[&>button]:flex-auto">
          <Button icon={ListChecks} onClick={() => onStatus(r, 'in_registry')}>
            {LABEL.setRegistry}
          </Button>
          {r.status !== 'rejected' && (
            <Button icon={Ban} onClick={() => onStatus(r, 'rejected')}>
              {LABEL.reject}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

export function RequestsView({ tzOffset }: { tzOffset: string }) {
  const { role } = useRole();
  const [editing, setEditing] = useState<{ request: RuleRequestView; mode: Mode } | null>(null);
  // «Описать правило» — администратор правил и утверждающий; статус и номер — администратор системы.
  const canDescribe = role === 'rules_admin' || role === 'approver';
  const isTeam = role === 'sysadmin';
  const q = useQuery({
    queryKey: rulesKeys.requests(role),
    queryFn: async () => {
      try {
        return await api.ruleRequests();
      } catch (e) {
        if (shouldReport(e)) reportClientError('Rules', 'load rule requests', e);
        throw e;
      }
    },
  });
  const list = q.data ?? [];

  let body: ReactNode;
  if (q.isLoading)
    body = (
      <div aria-busy="true" aria-label={LABEL.loading} className="space-y-3">
        <Skeleton className="h-20 w-full rounded-card" />
        <Skeleton className="h-20 w-full rounded-card" />
      </div>
    );
  else if (q.error)
    body = <LoadError error={q.error} title={LABEL.requestsError} onRetry={() => void q.refetch()} />;
  else if (list.length === 0)
    body = (
      <Card>
        <EmptyState icon={Inbox} title={T.requestsEmpty} />
      </Card>
    );
  else
    body = (
      <Card>
        <ul aria-label={LABEL.tabRequests} className="divide-y divide-line">
          {list.map((r) => (
            <Request
              key={r.id}
              r={r}
              tzOffset={tzOffset}
              onStatus={isTeam ? (request, mode) => setEditing({ request, mode }) : null}
            />
          ))}
        </ul>
      </Card>
    );

  return (
    <div className="space-y-4">
      {canDescribe && (
        <Card className="px-5 py-4 max-sm:px-4">
          <DescribeRule />
        </Card>
      )}
      <p className="text-[13px] text-ink-2">{T.requestsIntro}</p>
      {body}
      <GuardedModal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.mode === 'rejected' ? LABEL.rejectTitle : LABEL.registryTitle}
        width="w-[420px]"
      >
        {(ctl) =>
          editing && (
            <StatusBody
              key={`${editing.request.id}|${editing.mode}`}
              request={editing.request}
              mode={editing.mode}
              ctl={ctl}
              onDone={() => {
                ctl.setDirty(false);
                setEditing(null);
              }}
            />
          )
        }
      </GuardedModal>
    </div>
  );
}
